import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { MenuItem } from "@mui/material";
import moment from "moment";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faSearch } from "@fortawesome/free-solid-svg-icons";

/** COMPONENT */
import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";
import InputTextField from "../../../component/input/inputTextField";
import InputDatePicker from "../../../component/input/inputDatePicker";
import FilterSelect from "../../../component/select/filterSelect";
import AutocompleteSelect from "../../../component/select/autoCompleteSelect";
import ButtonCustom from "../../../component/button/buttonCustom";
import Loading from "../../../component/loading/loading";
import { notiError } from "../../../component/notification/notifications";

/** API */
import ReportApi from "../../../api/reports/reports.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import { numberFormat } from "../../../utils/common";
import { getFileExtension } from "../../../utils/fileTypeDownload";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/api-error.utils";
import {
  EMPTY_PAYMENT_LIST_SUMMARY,
  INVOICE_AMOUNT_DISPLAY,
  InvoiceAmountDisplay,
  PaymentListRow,
  PaymentListSummary,
} from "./paymentList.types";
import { PAYMENT_LIST_FILTER_ALL, usePaymentListFilter } from "./usePaymentListFilter";

/** STYLE */
import * as Styles from "../report.style";
import { stallLabel } from "../../../utils/stall.utils";

const DASH = "-";

/**
 * ว่างต้องได้ "" จริง ๆ — จึงใช้ option TEXT + helper นี้ ไม่ใช้ option PRICE ของ TableRowCommon
 * เพราะ PRICE เรียก numberFormat ตรง ๆ: numberFormat(null) = "0.00", numberFormat(undefined) = "NaN"
 * ค่าปรับ 0.00 ในแถวที่ไม่ควรมีค่าปรับ อ่านผิดความหมายทางบัญชี
 */
const money = (value: any) =>
  value === null || value === undefined || value === "" ? "" : numberFormat(Number(value) || 0);

/**
 * แถวนี้ควรโชว์ยอดต่อใบ (ค่าปรับ / รวมทั้งหมด) หรือเว้นว่าง
 * สลับพฤติกรรมได้ที่ INVOICE_AMOUNT_DISPLAY ใน paymentList.types.ts
 */
const showInvoiceAmount = (row: PaymentListRow) =>
  INVOICE_AMOUNT_DISPLAY === InvoiceAmountDisplay.EVERY_ROW || row.isFirstRowOfInvoice;

/** style ของแถว "รวมทั้งสิ้น" ให้แยกออกจากแถวข้อมูล */
const summaryCellStyle = {
  fontWeight: 600,
  backgroundColor: colors.extraLightGray,
};

/**
 * รายงาน "รายการชำระเงิน" — ใบแจ้งหนี้ที่ชำระแล้ว แยกรายประเภทค่าบริการ
 *
 * self-contained: ถือตัวกรอง ตาราง pagination และปุ่ม Excel ของตัวเอง ไม่รับ props จากหน้าแม่
 * (ต่างจากแท็บอื่นที่รับ date_start/date_end/search_type มา) เพราะตัวกรองชุดนี้ไม่ตรงกับ
 * filter row ที่อีก 5 แท็บใช้ร่วมกัน และต้องมี pagination เป็นของตัวเอง
 */
const PaymentListReport = () => {
  const filter = usePaymentListFilter();
  const { appliedParams, page, pageLimit } = filter;

  const [data, setData] = useState<PaymentListRow[]>([]);
  const [summary, setSummary] = useState<PaymentListSummary>(EMPTY_PAYMENT_LIST_SUMMARY);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  /**
   * dep มีแค่ appliedParams/page/pageLimit — ไม่มีค่า draft เลย
   * ผู้ใช้พิมพ์หรือเลือก dropdown จึงไม่ยิง API จนกว่าจะกดค้นหา (ตามดีไซน์ที่มีปุ่มค้นหา)
   */
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await ReportApi.getPaymentList({ ...appliedParams, page, pageLimit });
      const payload = res?.data;
      const rows: PaymentListRow[] = Array.isArray(payload?.data) ? payload.data : [];
      const nextSummary: PaymentListSummary = payload?.summary ?? EMPTY_PAYMENT_LIST_SUMMARY;

      setData(rows);
      setSummary(nextSummary);
      // ต้องเป็นจำนวน "แถว" ไม่ใช่ totalInvoices ไม่งั้นปุ่มหน้าขาด/เกิน
      setRowCount(Number(payload?.total ?? nextSummary.totalRows ?? 0));
    } catch (error) {
      setData([]);
      setSummary(EMPTY_PAYMENT_LIST_SUMMARY);
      setRowCount(0);
      // 404 = ไม่มีข้อมูลตามเงื่อนไขที่กรอง ไม่ใช่ error ให้ตารางโชว์ empty state เงียบ ๆ
      if (getApiErrorStatus(error) !== 404) {
        notiError(getApiErrorMessage(error, "ดึงรายการชำระเงินไม่สำเร็จ"));
      }
    } finally {
      setIsLoading(false);
    }
  }, [appliedParams, page, pageLimit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * ไฟล์ใช้ตัวกรองที่ apply แล้วเหมือนตาราง แต่ไม่ส่ง page/pageLimit
   * (ไฟล์เอาทุกแถวที่ผ่าน filter ไม่ได้ตัดเฉพาะหน้าที่เปิดอยู่)
   * ยิงผ่าน axios instance เพื่อให้ติด Authorization: Bearer ไปด้วย ไม่ใช้ window.open
   */
  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const res = await ReportApi.downloadPaymentListReport(appliedParams);
      // ต้องเช็คก่อนแตะ res.data — handleDownloadFile ของหน้าแม่พลาดจุดนี้แล้ว TypeError เงียบ ๆ
      if (!res || res.status >= 300) throw new Error("download payment list failed");

      const contentType = res.headers?.["content-type"];
      const blob = new Blob([res.data], { type: contentType });
      const fileName = `รายการชำระเงิน_${moment().format("YYYYMMDDHHmmss")}.${getFileExtension(
        contentType || ""
      )}`;
      saveAs(blob, fileName);
    } catch (error) {
      // responseType arraybuffer ทำให้ body ของ error เป็น ArrayBuffer อ่านข้อความไม่ได้
      notiError("ดาวน์โหลดไฟล์ Excel ไม่สำเร็จ");
    } finally {
      setIsDownloading(false);
    }
  };

  /** ใส่ align ให้ครบทุกคอลัมน์ — ไม่ใส่แล้ว TableCustom จะเดาจาก id เอง */
  const headCells = [
    { id: "seq", disablePadding: false, label: "ลำดับ", align: "center", width: 70 },
    { id: "invoiceDate", disablePadding: false, label: "วันที่ชำระเงิน", align: "center", width: 110 },
    { id: "periodDate", disablePadding: false, label: "งวดวันที่", align: "center", width: 110 },
    {
      id: "invoiceNo",
      disablePadding: false,
      label: "เลขที่ใบแจ้งหนี้",
      align: "center",
      width: 150,
    },
    { id: "customerName", disablePadding: false, label: "ชื่อผู้เช่าแผงค้า", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "center" },
    // หัวเดิมคือ "เลขที่แผงค้า" แต่ช่องนี้ fallback ไปโชว์ชื่อร้านเมื่อไม่มีเลข ทำให้ค่าไม่ตรงหัว
    // เปลี่ยนเป็นช่อง "แผงค้า" ที่โชว์ทั้งชื่อและเลขตามมาตรฐาน (รายงานนี้มีคอลัมน์แผงค้าช่องเดียว)
    { id: "stallNumber", disablePadding: false, label: "แผงค้า", align: "center" },
    { id: "description", disablePadding: false, label: "รายละเอียด", align: "center" },
    { id: "dueDate", disablePadding: false, label: "กำหนดวันชำระ", align: "center", width: 130 },
    { id: "amount", disablePadding: false, label: "จำนวนเงิน", align: "right", width: 120 },
    { id: "fineAmount", disablePadding: false, label: "ค่าปรับ", align: "right", width: 110 },
    { id: "totalAmount", disablePadding: false, label: "รวมทั้งหมด", align: "right", width: 130 },
  ];

  const renderRow = (row: PaymentListRow) => {
    const objRenderData = {
      // invoiceId ซ้ำได้หลายแถว (1 แถว = 1 ประเภทบริการ) key จึงต้องผสมให้ไม่ซ้ำ
      key: `${row.invoiceId}-${row.paymentServiceId ?? "x"}-${row.seq}`,
      id: String(row.invoiceId),
      obj: row,
      columns: [
        // seq ต่อเนื่องข้ามหน้ามาจาก backend แล้ว ห้ามบวก offset ของหน้าซ้ำ
        { option: "TEXT", align: "center", label: row.seq },
        // *Text เป็น dd/MM/พ.ศ. มาแล้ว เอาไปแสดงตรง ๆ ห้าม format ใหม่ฝั่ง front
        // { option: "TEXT", align: "center", label: row.invoiceDateText || DASH },
        { option: "TEXT", align: "center", label: row.dateTime || DASH },
        { option: "TEXT", align: "center", label: row.periodDateText || DASH },
        { option: "TEXT", align: "center", label: row.invoiceNo || DASH },
        { option: "TEXT", align: "left", label: row.customerName || DASH },
        { option: "TEXT", align: "center", label: row.zoneName || DASH },
        { option: "TEXT", align: "center", label: stallLabel(row, DASH) },
        { option: "TEXT", align: "center", label: row.description || DASH },
        {
          option: "TEXT",
          align: "center",
          label: row.dueDateText || DASH,
          style: { color: colors.red },
        },
        { option: "TEXT", align: "right", label: money(row.amount) },
        /**
         * ค่าปรับ/รวมทั้งหมด เป็นยอดของ "ทั้งใบ" ซ้ำเหมือนกันทุกแถวของใบเดียวกัน
         * ค่า default คือโชว์แถวแรกของใบเท่านั้น (ตาม doc API และตรงกับไฟล์ Excel)
         * สลับเป็นโชว์ทุกแถวตามภาพดีไซน์ได้ที่ INVOICE_AMOUNT_DISPLAY
         */
        {
          option: "TEXT",
          align: "right",
          label: showInvoiceAmount(row) ? money(row.fineAmount) : "",
        },
        {
          option: "TEXT",
          align: "right",
          label: showInvoiceAmount(row) ? money(row.totalAmount) : "",
        },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  /**
   * แถว "รวมทั้งสิ้น" — TableCustom ไม่มี TableFooter ให้ใช้ จึงดันเป็น body row ท้ายสุด
   * ยอดอ่านจาก summary เท่านั้น (คิดจากผลลัพธ์ทั้งชุดที่ผ่าน filter ไม่ใช่แค่หน้าที่เปิด
   * และไม่นับค่าปรับซ้ำ) ห้ามบวกจาก data เอง
   */
  const renderSummaryRow = () => (
    <TableRowCommon
      key="payment-list-summary"
      id="payment-list-summary"
      obj={{}}
      columns={[
        // เซลล์ว่างคลุมคอลัมน์ 1-8 · 8 + 1 + 3 = 12 คอลัมน์พอดี
        { option: "MERGE", colSpan: 8, style: summaryCellStyle },
        { option: "TEXT", align: "right", label: "รวมทั้งสิ้น", style: summaryCellStyle },
        { option: "TEXT", align: "right", label: money(summary.sumAmount), style: summaryCellStyle },
        {
          option: "TEXT",
          align: "right",
          label: money(summary.sumFineAmount),
          style: summaryCellStyle,
        },
        {
          option: "TEXT",
          align: "right",
          label: money(summary.sumTotalAmount),
          style: summaryCellStyle,
        },
      ]}
    />
  );

  /**
   * ไม่มีข้อมูล = ต้องเป็น array ว่างล้วน ห้ามใส่แถวรวม
   * TableCustom เช็ค !rowsData.length เพื่อโชว์ textEmptyData ถ้าใส่แถวรวมไว้
   * ตารางจะดูเหมือนมีข้อมูล 1 แถวยอด 0.00 แล้ว empty state หายไปเลย
   */
  const bodyRows = data.length ? [...data.map(renderRow), renderSummaryRow()] : [];

  return (
    <div>
      {isLoading && <Loading show type="softLoading" />}

      <Row className="align-items-end">
        <Col lg={3} md={6} xs={12} className="my-1">
          <InputTextField
            value={filter.keyword}
            placeholder="ค้นหา"
            onchange={(e: any) => filter.setKeyword(e.target.value)}
            // กด Enter ในช่องค้นหา = กดปุ่มค้นหา ไม่ต้องเลื่อนไปคลิก
            onkeyup={(e: any) => e.key === "Enter" && filter.applySearch()}
          />
        </Col>
        <Col lg={3} md={6} xs={12} className="my-1">
          {/*
            ใช้ autocomplete ไม่ใช่ select ธรรมดาเพราะแผงค้าในสาขาเดียวมีหลายร้อยรายการ เลื่อนหาไม่ไหว
            (โซนใช้แบบเดียวกันเพื่อให้แถวตัวกรองหน้าตาเสมอกัน · ความสูงกล่องเท่า FilterSelect อยู่แล้ว)
            state ในฮุคยังเก็บเป็น id เหมือนเดิม แปลง id <-> option object ที่จุดนี้จุดเดียว
          */}
          <AutocompleteSelect
            labelId="payment-list-zone"
            options={filter.zones}
            // ต้องเป็น null ไม่ใช่ undefined ไม่งั้น MUI สลับไปโหมด uncontrolled แล้วค่าค้าง
            value={filter.zones.find((zone) => zone.zoneId === filter.zoneId) || null}
            getOptionLabel={(option: any) => option?.zoneName || DASH}
            isOptionEqualToValue={(option: any, value: any) => option?.zoneId === value?.zoneId}
            onChange={(event: any, value: any) =>
              filter.handleChangeZone(value?.zoneId ?? PAYMENT_LIST_FILTER_ALL)
            }
            placeholder="ค้นหาโซน"
            noOptionsText="ไม่พบโซน"
          />
        </Col>
        <Col lg={3} md={6} xs={12} className="my-1">
          <AutocompleteSelect
            labelId="payment-list-stall"
            // ลิสต์นี้ถูกกรองตามโซนที่เลือกแล้ว (ยังไม่เลือกโซน = แผงของทุกโซน)
            options={filter.stallOptions}
            value={filter.stallOptions.find((stall) => stall.stallId === filter.stallId) || null}
            getOptionLabel={(option: any) => (option ? filter.stallOptionLabel(option) : "")}
            // ค้นได้ทั้งเลขแผงและชื่อร้าน — ไม่ระบุ stringify เอง MUI จะกรองด้วย label เท่านั้น
            filterOptions={(option: any) => `${option?.stallNumber || ""} ${option?.stallName || ""}`}
            isOptionEqualToValue={(option: any, value: any) => option?.stallId === value?.stallId}
            onChange={(event: any, value: any) =>
              filter.setStallId(value?.stallId ?? PAYMENT_LIST_FILTER_ALL)
            }
            placeholder="ค้นหาแผงค้า"
            noOptionsText="ไม่พบแผงค้า"
          />
        </Col>
        <Col lg={3} md={6} xs={12} className="my-1">
          {/* ไม่มีตัวเลือก "ค่าปรับ" (paymentServiceId 5) — ค่าปรับเป็นคอลัมน์ของตัวเอง ฮุคกรองออกให้แล้ว */}
          <FilterSelect
            labelId="payment-list-payment-type"
            selectId="payment-list-payment-type"
            value={filter.paymentServiceId}
            onchange={(event: any) => filter.setPaymentServiceId(event.target.value)}
            // displayEmpty ต้องส่งด้วย ไม่งั้น MUI ไม่เรียก renderValue ตอนยังไม่เลือก
            displayEmpty
            renderValue={() =>
              filter.paymentServiceId === PAYMENT_LIST_FILTER_ALL ? (
                // ยังไม่เลือก = โชว์ placeholder สีเทาให้เสมอกับอีก 3 ช่องในแถวเดียวกัน
                <span style={{ color: colors.inputProps }}>ประเภท</span>
              ) : (
                filter.paymentTypeLabel
              )
            }
            options={[
              <MenuItem key={PAYMENT_LIST_FILTER_ALL} value={PAYMENT_LIST_FILTER_ALL}>
                ทั้งหมด
              </MenuItem>,
              ...filter.paymentTypes.map((type) => (
                <MenuItem key={type.paymentServiceId} value={type.paymentServiceId}>
                  {type.paymentName || DASH}
                </MenuItem>
              )),
            ]}
          />
        </Col>
      </Row>

      <Row className="align-items-end">
        <Col lg={3} md={6} xs={12} className="my-1">
          <InputDatePicker
            value={filter.dateStart || null}
            onChange={filter.handleChangeDateStart}
            onClear={() => filter.handleChangeDateStart(null)}
            allowClear
            placeholder="เลือกวันที่"
            disableEndDate={filter.dateEnd || undefined}
          />
        </Col>
        <Col lg={3} md={6} xs={12} className="my-1">
          <InputDatePicker
            value={filter.dateEnd || null}
            onChange={filter.handleChangeDateEnd}
            onClear={() => filter.handleChangeDateEnd(null)}
            allowClear
            placeholder="เลือกวันที่"
            disableStartDate={filter.dateStart || undefined}
          />
        </Col>
        <Col lg={3} md={6} xs={6} className="my-1">
          <ButtonCustom
            textButton="ค้นหา"
            startIcon={<FontAwesomeIcon icon={faSearch} />}
            loading={isLoading}
            onClick={filter.applySearch}
            style={{ width: "100%" }}
          />
        </Col>
        <Col lg={3} md={6} xs={6} className="my-1">
          <ButtonCustom
            textButton="EXCEL"
            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
            loading={isDownloading}
            onClick={handleDownloadExcel}
            style={{
              width: "100%",
              "&.MuiButton-contained": {
                backgroundColor: `${colors.lightOrange} !important`,
                ":hover": {
                  backgroundColor: `${colors.lightOrange} !important`,
                },
              },
            }}
          />
        </Col>
      </Row>

      <Styles.TableContainer>
        <TableCustom
          headCells={headCells}
          customScroll
          page={page}
          pageLimit={pageLimit}
          // API ยังไม่รองรับการเรียงลำดับ หัวตารางจึงไม่มีปุ่ม sort (props ยัง required อยู่)
          sortType="ASC"
          sortBy=""
          rowCount={rowCount}
          rowsData={bodyRows}
          onSort={() => {}}
          setPageLimit={filter.handleChangePageLimit}
          setPage={(value: number) => filter.setPage(value)}
          textEmptyData="ไม่พบรายการชำระเงิน"
          // 12 คอลัมน์ + whiteSpace nowrap ที่ TableRowCommon บังคับไว้ ต้องมี scroll แนวนอน
          tableMinWidth={2000}
        />
      </Styles.TableContainer>
    </div>
  );
};

export default PaymentListReport;
